import React, { FunctionComponent, ReactNode, useCallback, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { NumberBadge } from '../../atoms/icons/NumberBadge'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { Accordion } from '../../molecules/accordion/Accordion'
import {
  ClientInvitationRow,
  type ClientInvitationRowProps,
} from '../../molecules/clientInvitationRow/ClientInvitationRow'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'

export type AddNewClientsAccordionProps = {
  clientInvitations: ClientInvitationRowProps[]
  registeredCount: number
  addNewClientButton: ReactNode
  testIDPrefix?: string
}

const HeaderRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  flex: 1,
})

const TitleGroup = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const RegisteredCountBadge = styled(NumberBadge)(({ theme }) => ({
  alignSelf: 'center',
  borderRadius: theme.spacing['4px'],
}))

const AccessoryContainer = styled.View({
  marginLeft: 'auto',
})

const ListContainer = styled.View(({ theme }) => ({
  borderTopWidth: 1,
  borderTopColor: theme.colors.borderDefault,
}))

const EmptyStateContainer = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: theme.spacing['24px'],
}))

export const AddNewClientsAccordion: FunctionComponent<AddNewClientsAccordionProps> = ({
  clientInvitations,
  registeredCount,
  addNewClientButton,
  testIDPrefix = 'InvitedClientsAccordion',
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const hasInitiallyOpened = useRef(false)

  const handleContentLayout = useCallback(() => {
    if (registeredCount > 0 && !hasInitiallyOpened.current) {
      hasInitiallyOpened.current = true
      setIsOpen(true)
    }
  }, [registeredCount])

  const chevronIcon = (
    <ChevronIcon
      size={24}
      direction={isOpen ? ChevronIconDirection.UP : ChevronIconDirection.DOWN}
      fillColor={theme.colors.iconDefault}
    />
  )

  const titleContent = (
    <HeaderRow>
      <TitleGroup>
        <FormattedMessage
          defaultMessage='Invited clients'
          description='Title for the invited clients accordion section'
        />
        {registeredCount > 0 && (
          <RegisteredCountBadge
            number={registeredCount}
            size={16}
            lineHeight={12}
            backgroundColor={theme.colors.backgroundActive}
            color={theme.colors.textInverse}
            typeStyle={{ fontSize: 12, fontWeight: '700' }}
          />
        )}
        <Tooltip
          content={
            <FormattedMessage
              defaultMessage='Clients must complete their registration before they can be scheduled. Once scheduled, they will automatically appear on your main client list.'
              description='Tooltip explaining the invited clients registration process'
            />
          }
          hoverEnabled
          accessibilityLabel={intl.formatMessage({
            defaultMessage: 'Information about invited clients',
            description: 'Accessibility label for the invited clients info tooltip',
          })}
          testID={tID(`${testIDPrefix}-info-tooltip`)}
        >
          <InfoIcon size={16} fillColor={theme.colors.iconDefault} />
        </Tooltip>
      </TitleGroup>
      <AccessoryContainer>{addNewClientButton}</AccessoryContainer>
    </HeaderRow>
  )

  const accordionContent =
    clientInvitations.length > 0 ? (
      <ListContainer onLayout={handleContentLayout}>
        {clientInvitations.map((invitation) => (
          <ClientInvitationRow key={invitation.id} {...invitation} />
        ))}
      </ListContainer>
    ) : (
      <EmptyStateContainer testID={tID(`${testIDPrefix}-empty-state`)}>
        <BodyText
          text={
            <FormattedMessage
              defaultMessage='No invited clients at this time'
              description='Empty state message when there are no invited clients to display'
            />
          }
          size={BodyTextSize.SMALL}
          color={theme.colors.textSecondary}
        />
      </EmptyStateContainer>
    )

  return (
    <Accordion
      title={titleContent}
      content={accordionContent}
      iconComponent={chevronIcon}
      hideExpansionIcon
      showGradient={false}
      isExpanded={isOpen}
      handleIsExpandedChanged={setIsOpen}
      testIDPrefix={testIDPrefix}
      titleTextContainerStyle={{ flex: 1 }}
      titleProps={{ style: { display: 'flex', width: '100%' } }}
      style={{
        paddingTop: 0,
        ...(!isOpen && {
          marginBottom: theme.spacing['16px'],
          borderBottomWidth: 1,
          borderBottomColor: theme.colors.borderDefault,
        }),
      }}
    />
  )
}
